/**
 * The user's private ingredient library — fermentables, hops, yeast and
 * miscellaneous additions that the shipped catalog does not have.
 *
 * PLAN §6: *"users can define private custom ingredients."* Recipe items already
 * allow a one-off (`catalogRef: null`). This table is the *reusable* half of
 * that: a named spec the brewer (or the assistant) writes once and picks again.
 *
 * ## Not the catalog
 *
 * These documents live in the command log, per user. They are never merged into
 * `@beerwright/catalog`, never share a slug with a catalog entry, and updating
 * one cannot move an old recipe — adding to a recipe still *snapshots* the
 * numbers (see `../bridge/snapshot.ts`), the same rule a catalog entry obeys.
 *
 * Styles are not user-definable here. A one-off spice can still go on a recipe
 * as `catalogRef: null` without a library row; this table is for the ones they
 * want to pick again.
 */

import { z } from 'zod';
import { CUSTOM_INGREDIENT_SCHEMA_VERSION } from '../version';
import {
  documentIdSchema,
  fermentableClassSchema,
  maltPhClassSchema,
  miscFormSchema,
  miscTypeSchema,
  miscUseSchema,
  nameSchema,
  notesSchema,
  percentSchema,
  timestampSchema,
  yeastFormSchema,
  yeastTypeSchema,
} from './common';

// ---------------------------------------------------------------------------
// Kinds
// ---------------------------------------------------------------------------

/** The four library kinds. Styles are not user-definable here. */
export const customIngredientKindSchema = z.enum(['fermentable', 'hop', 'yeast', 'misc']);

/** @see customIngredientKindSchema */
export type CustomIngredientKind = z.infer<typeof customIngredientKindSchema>;

/** Every library kind, in the order the ingredients page groups them. */
export const CUSTOM_INGREDIENT_KINDS: readonly CustomIngredientKind[] = [
  'fermentable',
  'hop',
  'yeast',
  'misc',
];

/** What the hop is normally bought for. Dual means it earns both jobs. */
export const customHopPurposeSchema = z.enum(['bittering', 'aroma', 'dual']);

/** @see customHopPurposeSchema */
export type CustomHopPurpose = z.infer<typeof customHopPurposeSchema>;

// ---------------------------------------------------------------------------
// Shared identity
// ---------------------------------------------------------------------------

const identity = {
  /** Document schema version; see {@link CUSTOM_INGREDIENT_SCHEMA_VERSION}. */
  schemaVersion: z.number().int().min(1),
  /** Stable id, unique within the workspace. */
  id: documentIdSchema,
  /** Display name. */
  name: nameSchema,
  /** Brewer's note: supplier, lot, why this exists. */
  notes: notesSchema,
  /** Creation time, **ms since epoch**, from the creating command's envelope. */
  createdAt: timestampSchema,
  /** Last-modification time, **ms since epoch**, from the last command's envelope. */
  updatedAt: timestampSchema,
};

// ---------------------------------------------------------------------------
// Per-kind documents
// ---------------------------------------------------------------------------

/**
 * A custom fermentable.
 *
 * Fields are the snapshot the engine calculates with — the same set
 * `snapshotFermentable` copies out of the catalog — so adding this to a recipe
 * does not invent numbers at the last moment.
 */
export const customFermentableDocSchema = z.strictObject({
  ...identity,
  kind: z.literal('fermentable'),
  /** Maltster or brand, or `null` when there is none. */
  maker: z.string().min(1).max(60).nullable(),
  /** Behaviour class — drives which efficiency core applies. */
  class: fermentableClassSchema,
  /** Which titration curve the mash-pH model uses. */
  maltClass: maltPhClassSchema,
  /** Fine-grind dry-basis extract, **% of dry mass**. */
  extractFineGrindDryBasisPercent: percentSchema,
  /** Moisture as shipped, **% of as-is mass**. */
  moisturePercent: percentSchema,
  /** Colour, **degrees Lovibond**. */
  colorLovibond: z.number().finite().min(0).max(700),
  /** Diastatic power, **°Lintner**. Unused by the vitals maths; carried for the UI. */
  diastaticPowerLintner: z.number().finite().min(0).max(300),
  /** Measured distilled-water mash pH, or `null` for the class default. Never guessed. */
  distilledWaterPh: z.number().finite().min(3).max(7).nullable(),
  /** Measured buffering capacity, **mEq/kg/pH**, or `null` for the class default. */
  bufferingCapacityMEqPerKgPerPh: z.number().finite().min(-800).max(800).nullable(),
  /** Acidulated malt's lactic acid content, **% by mass**, or `null`. */
  acidContentPercentLactic: percentSchema.nullable(),
});

/** @see customFermentableDocSchema */
export type CustomFermentableDoc = z.infer<typeof customFermentableDocSchema>;

/**
 * A custom hop.
 *
 * Alpha acid is a single number, not a published band: a home-grown or
 * farm-stand lot has one figure off the bag, and that is what the IBU maths
 * wants.
 */
export const customHopDocSchema = z.strictObject({
  ...identity,
  kind: z.literal('hop'),
  /** Growing region or country, or `null` when unknown. */
  origin: z.string().min(1).max(60).nullable(),
  /** What it is normally bought for. */
  purpose: customHopPurposeSchema,
  /** Alpha acid, **% w/w** — this lot's figure. */
  alphaAcidPercent: z.number().finite().min(0).max(30),
  /** Beta acid, **% w/w**, or `null` when unpublished. */
  betaAcidPercent: z.number().finite().min(0).max(20).nullable(),
  /** Total oil, **mL/100 g**, or `null` when unpublished. */
  totalOilMlPer100g: z.number().finite().min(0).max(6).nullable(),
});

/** @see customHopDocSchema */
export type CustomHopDoc = z.infer<typeof customHopDocSchema>;

/**
 * A custom yeast.
 *
 * `type` is core's vocabulary (`ale` / `lager` / `brett` / …), not the
 * catalog's (`wild` / `kveik` / `hybrid`): this document is a recipe snapshot
 * waiting to happen, so it speaks the engine's language.
 */
export const customYeastDocSchema = z.strictObject({
  ...identity,
  kind: z.literal('yeast'),
  /** Lab or brand, or `null` when it is a house culture. */
  lab: z.string().min(1).max(60).nullable(),
  /** Family. Drives core's default pitch rate. */
  type: yeastTypeSchema,
  /** Packaging form. Drives the viability decay model. */
  form: yeastFormSchema,
  /** Minimum apparent attenuation, **%**. */
  attenuationMinPercent: percentSchema,
  /** Maximum apparent attenuation, **%**. */
  attenuationMaxPercent: percentSchema,
  /** Minimum fermentation temperature, **°C**, or `null`. */
  temperatureMinC: z.number().finite().min(-10).max(60).nullable(),
  /** Maximum fermentation temperature, **°C**, or `null`. */
  temperatureMaxC: z.number().finite().min(-10).max(60).nullable(),
});

/** @see customYeastDocSchema */
export type CustomYeastDoc = z.infer<typeof customYeastDocSchema>;

/**
 * A custom miscellaneous addition — a house spice, a farm acid, a fining
 * the catalog does not list.
 *
 * Miscs feed no calculation (see `document/ingredients.ts`), so this is
 * identity plus the dosing facts a recipe row needs: type, typical stage,
 * physical form (which picks g / mL / each), optional contact time, and
 * solution strength for acids.
 */
export const customMiscDocSchema = z.strictObject({
  ...identity,
  kind: z.literal('misc'),
  /** What kind of thing it is. */
  type: miscTypeSchema,
  /** The stage it is normally added at. */
  use: miscUseSchema,
  /** Physical form as used — drives the default dosing unit. */
  form: miscFormSchema,
  /** Contact time at {@link use}, **minutes**, or `null` when timing is meaningless. */
  timingMinutes: z.number().finite().min(0).max(20160).nullable(),
  /** Solution strength, **% w/w** (88 for standard lactic), or `null` for solids. */
  concentrationPercent: z.number().finite().min(0.1).max(100).nullable(),
});

/** @see customMiscDocSchema */
export type CustomMiscDoc = z.infer<typeof customMiscDocSchema>;

/** One library entry. Discriminated on {@link CustomIngredientDoc.kind}. */
export const customIngredientDocSchema = z.discriminatedUnion('kind', [
  customFermentableDocSchema,
  customHopDocSchema,
  customYeastDocSchema,
  customMiscDocSchema,
]);

/** @see customIngredientDocSchema */
export type CustomIngredientDoc = z.infer<typeof customIngredientDocSchema>;

// ---------------------------------------------------------------------------
// The two spellings
// ---------------------------------------------------------------------------

/**
 * The maker a library entry names, under whichever key its kind keeps it.
 *
 * A fermentable has a `maker`, a yeast has a `lab`, and a hop has an origin
 * rather than a maker — "United States" in a column headed Maker would be a
 * category error, so hops and miscs answer `null`. One function, so the lot a
 * reducer fills, the lot the add dialog prefills and the row the backfill
 * planner compares all read the same column.
 */
export function customIngredientMaker(doc: CustomIngredientDoc): string | null {
  switch (doc.kind) {
    case 'fermentable':
      return doc.maker;
    case 'yeast':
      return doc.lab;
    default:
      return null;
  }
}

/**
 * The name a *recipe row* built from this entry carries: `"Weyermann Pilsner"`.
 *
 * Recipe items have no maker field (`document/ingredients.ts`), so the maker is
 * folded into the name at snapshot time, exactly as the catalog bridge folds a
 * catalog maker in. A lot is the other way round — bare name, maker in its own
 * column — which is why the two never matched by name for a custom malt with a
 * maker, and why the backfill planner compares each side in its own convention.
 * `Generic` and a maker the name already starts with are dropped, matching
 * `displayName` in `bridge/snapshot.ts` byte for byte.
 */
export function customIngredientDisplayName(doc: CustomIngredientDoc): string {
  const maker = customIngredientMaker(doc);
  if (maker === null || maker === 'Generic' || doc.name.startsWith(maker)) return doc.name;
  return `${maker} ${doc.name}`;
}

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

interface BuildBase {
  readonly id: string;
  readonly name: string;
  readonly timestamp: number;
  readonly notes?: string;
}

/** Arguments for {@link buildCustomIngredientDoc}. */
export type BuildCustomIngredientArgs =
  | (BuildBase & {
      readonly kind: 'fermentable';
      readonly maker?: string | null;
      readonly class?: CustomFermentableDoc['class'];
      readonly maltClass?: CustomFermentableDoc['maltClass'];
      readonly extractFineGrindDryBasisPercent?: number;
      readonly moisturePercent?: number;
      readonly colorLovibond?: number;
      readonly diastaticPowerLintner?: number;
      readonly distilledWaterPh?: number | null;
      readonly bufferingCapacityMEqPerKgPerPh?: number | null;
      readonly acidContentPercentLactic?: number | null;
    })
  | (BuildBase & {
      readonly kind: 'hop';
      readonly origin?: string | null;
      readonly purpose?: CustomHopPurpose;
      readonly alphaAcidPercent: number;
      readonly betaAcidPercent?: number | null;
      readonly totalOilMlPer100g?: number | null;
    })
  | (BuildBase & {
      readonly kind: 'yeast';
      readonly lab?: string | null;
      readonly type?: CustomYeastDoc['type'];
      readonly form?: CustomYeastDoc['form'];
      readonly attenuationMinPercent?: number;
      readonly attenuationMaxPercent?: number;
      readonly temperatureMinC?: number | null;
      readonly temperatureMaxC?: number | null;
    })
  | (BuildBase & {
      readonly kind: 'misc';
      readonly type?: CustomMiscDoc['type'];
      readonly use?: CustomMiscDoc['use'];
      readonly form?: CustomMiscDoc['form'];
      readonly timingMinutes?: number | null;
      readonly concentrationPercent?: number | null;
    });

/**
 * Build a custom-ingredient document.
 *
 * Pure: identity and time come from the caller, exactly as
 * {@link buildInventoryItemDoc} does. Kind-specific numbers default to honest
 * blanks (a pale base malt, a dual-purpose hop, an ale dry yeast) rather than
 * to someone else's spec sheet — the user or the assistant is expected to
 * overwrite the ones they know. A custom misc defaults to a boil spice.
 */
export function buildCustomIngredientDoc(args: BuildCustomIngredientArgs): CustomIngredientDoc {
  const identityFields = {
    schemaVersion: CUSTOM_INGREDIENT_SCHEMA_VERSION,
    id: args.id,
    name: args.name,
    notes: args.notes ?? '',
    createdAt: args.timestamp,
    updatedAt: args.timestamp,
  };

  switch (args.kind) {
    case 'fermentable':
      return {
        ...identityFields,
        kind: 'fermentable',
        maker: args.maker ?? null,
        class: args.class ?? 'grain',
        maltClass: args.maltClass ?? 'base',
        extractFineGrindDryBasisPercent: args.extractFineGrindDryBasisPercent ?? 80,
        moisturePercent: args.moisturePercent ?? 4,
        colorLovibond: args.colorLovibond ?? 2,
        diastaticPowerLintner: args.diastaticPowerLintner ?? 0,
        distilledWaterPh: args.distilledWaterPh ?? null,
        bufferingCapacityMEqPerKgPerPh: args.bufferingCapacityMEqPerKgPerPh ?? null,
        acidContentPercentLactic: args.acidContentPercentLactic ?? null,
      };
    case 'hop':
      return {
        ...identityFields,
        kind: 'hop',
        origin: args.origin ?? null,
        purpose: args.purpose ?? 'dual',
        alphaAcidPercent: args.alphaAcidPercent,
        betaAcidPercent: args.betaAcidPercent ?? null,
        totalOilMlPer100g: args.totalOilMlPer100g ?? null,
      };
    case 'yeast':
      return {
        ...identityFields,
        kind: 'yeast',
        lab: args.lab ?? null,
        type: args.type ?? 'ale',
        form: args.form ?? 'dry',
        attenuationMinPercent: args.attenuationMinPercent ?? 73,
        attenuationMaxPercent: args.attenuationMaxPercent ?? 77,
        temperatureMinC: args.temperatureMinC ?? null,
        temperatureMaxC: args.temperatureMaxC ?? null,
      };
    case 'misc':
      return {
        ...identityFields,
        kind: 'misc',
        type: args.type ?? 'spice',
        use: args.use ?? 'boil',
        form: args.form ?? 'solid',
        timingMinutes: args.timingMinutes ?? null,
        concentrationPercent: args.concentrationPercent ?? null,
      };
  }
}
